import { Amplify } from "aws-amplify";
import { fetchAuthSession, signOut } from "aws-amplify/auth";

import { clearCognitoSessionMarker } from "./auth";

/**
 * Amplify bootstrap for the Cognito-backed login path.
 *
 * The Amplify_Login_UI (`<Authenticator>` on the `/login` route) authenticates
 * email/password against the Stage's single shared Cognito_User_Pool. There is
 * exactly one Cognito_User_Pool + app client per deployment Stage, shared across
 * every Tenant in that Stage, so the pool id / app client id / region are
 * per-Stage build config, not per-Tenant runtime values.
 *
 * The pool id and app client id correspond to the outputs exported by the
 * backend's `serverless.yml` resources (`CognitoUserPoolId`,
 * `CognitoUserPoolClientId`). They are consumed here through the same per-Stage
 * build-config mechanism the rest of the SPA already uses: Create React App
 * `REACT_APP_*` environment variables, inlined at build time and written to
 * `.env` per Stage by the deploy pipeline (mirroring how `REACT_APP_SERVER_URL`
 * is wired in `src/config/Config.js`). Nothing is hardcoded per Stage/Tenant in
 * source.
 *
 * Uses the Amplify JS v6 (`Amplify.configure({ Auth: { Cognito: {...} } })`)
 * configuration shape, matching `aws-amplify@^6` / `@aws-amplify/ui-react@^6`,
 * which support the app's React 18 version. In v6 the AWS region is derived from
 * the `userPoolId` prefix, so there is no separate region config field;
 * `REACT_APP_COGNITO_REGION` is retained only for the app-level RUM/telemetry
 * that references it, not for Amplify Auth.
 */

const {
  REACT_APP_COGNITO_USER_POOL_ID,
  REACT_APP_COGNITO_USER_POOL_CLIENT_ID,
  REACT_APP_COGNITO_REGION,
} = process.env;

/**
 * Builds the Amplify Auth configuration object from the per-Stage build config.
 * Kept pure (no side effects) and injectable so it can be unit-tested and reused
 * by the `/login` route's bootstrap.
 *
 * @param {object} [env=process.env] the environment to read the per-Stage Cognito
 *   values from (injectable for testing)
 * @returns {{ Auth: { Cognito: { userPoolId: string, userPoolClientId: string } } }}
 *   the Amplify v6 Auth configuration
 */
function buildAmplifyConfig(env = process.env) {
  return {
    Auth: {
      Cognito: {
        userPoolId: env.REACT_APP_COGNITO_USER_POOL_ID,
        userPoolClientId: env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID,
      },
    },
  };
}

/**
 * Runs the `Amplify.configure(...)` bootstrap for the Cognito login path.
 *
 * Idempotent and safe to import from the `/login` route module: the
 * bootstrap runs at app/login-route load, before `<Authenticator>` renders, so the
 * Amplify SDK is configured against the Stage's shared pool by the time any
 * sign-in/sign-up call is made.
 *
 * @param {object} [env=process.env] the environment to read per-Stage Cognito
 *   values from (injectable for testing)
 */
function configureAmplify(env = process.env) {
  // Amplify v6 derives the region from the `userPoolId` prefix (e.g.
  // "us-west-1_ABC..." -> us-west-1), so there is no separate region field on the
  // Cognito config. The second argument to `Amplify.configure` is `libraryOptions`
  // (SSR flag, custom headers, ...), NOT additional resource config — passing
  // `{ Auth: { region } }` there is silently ignored and must not be relied on.
  Amplify.configure(buildAmplifyConfig(env));
}

/**
 * Clears the local Amplify/Cognito session (the id/access/refresh tokens Amplify
 * stores in `localStorage`) as part of signing out of the Cognito login path.
 *
 * The backend `/signout` clears the Insights `ddl`/`ddl_o_t` cookies, but that
 * cannot touch the Cognito tokens Amplify keeps client-side. If those are left
 * behind, the next visit to `/login` finds `authStatus === "authenticated"` and
 * the `<Authenticator>` render-prop's `LoginExchange` silently re-mints a fresh
 * `ddl` cookie — i.e. the person is logged straight back in and never actually
 * signs out. Calling `signOut()` here removes that local session so sign-out
 * sticks.
 *
 * Runs `configureAmplify()` first because this executes from the app shell
 * (the `/signout` route), which may load without the `/login` module having
 * evaluated — so Amplify might not yet be configured, and `signOut()` would
 * otherwise throw "Auth UserPool not configured". `configureAmplify` is
 * idempotent.
 *
 * Best-effort by design: any failure is swallowed. A person who logged in via
 * the Canvas/LTI path has no Cognito session at all, so `signOut()` is a
 * no-op/expected-throw for them and must never block or fail the sign-out.
 *
 * Returns whether a Cognito session actually existed (detected via
 * `fetchAuthSession()` before signing out): `true` for a Cognito-login user,
 * `false` for a Canvas/LTI user (or when detection fails). Callers use this to
 * tailor the post-sign-out UX — a Cognito user is offered a "log in again" link
 * to `/login`, whereas an LTI user keeps the "close this tab / return to Canvas"
 * message (they have no Insights login page to return to).
 *
 * @param {object} [env=process.env] injectable env for `configureAmplify`
 * @returns {Promise<boolean>} whether a Cognito session was present
 */
async function clearCognitoSession(env = process.env) {
  let hadCognitoSession = false;
  try {
    configureAmplify(env);
    // Detect a Cognito session BEFORE clearing it: tokens present => the person
    // logged in via the Cognito path. LTI users have no tokens here.
    try {
      const session = await fetchAuthSession();
      hadCognitoSession = Boolean(session?.tokens?.idToken);
    } catch {
      hadCognitoSession = false;
    }
    await signOut();
  } catch (error) {
    if (typeof window !== "undefined" && window["ddlDebug"]) {
      console.error(
        "Amplify signOut during sign-out failed (ignored): ",
        error
      );
    }
  } finally {
    // Clear the Cognito-session marker cookie so a later session is not
    // misclassified as Cognito by the request helper's 401 handler. Done in a
    // `finally` (and independent of `hadCognitoSession`) so the marker is always
    // dropped on sign-out, even if the Amplify calls above throw.
    clearCognitoSessionMarker();
  }
  return hadCognitoSession;
}

export {
  buildAmplifyConfig,
  clearCognitoSession,
  configureAmplify,
  REACT_APP_COGNITO_USER_POOL_ID,
  REACT_APP_COGNITO_USER_POOL_CLIENT_ID,
  REACT_APP_COGNITO_REGION,
};
